import { formatNumber } from '@angular/common';

export interface ValidityDatasConfig {
  start?: ValidityDateTime;
  end?: ValidityDateTime;
  timezone?: ValidityTimezone;
}
export interface ValidityDateTime {
  date?: Date;
  time?: string;
  inclusive: boolean;
  timestamp?: number;
  day?: number;
  month?: number;
  year?: number;
}
export interface ValidityTimezone {
  value: string;
  offset: string;
}
export class ValidityDatas {
  start?: ValidityDateTime;
  end?: ValidityDateTime;
  timezone?: ValidityTimezone;
  state: string = 'pending';

  constructor(config?: ValidityDatasConfig | string, timezone?: string) {
    if(config===null){
      this.state = 'missing';
      return;
    }
    if (typeof config === 'string') {
      const result = /([\[\(])["]?([^"]*)["]?,["]?([^"]*)["]?([\]\)])/.exec(
        config
      );

      if (!result) {
        throw new Error('Invalid range');
      }
      const [, inclusiveMin, min, max, inclusiveMax] = result;
      config = {
        start: min ? this.parse(min, inclusiveMin === '[') : undefined,
        end: max ? this.parse(max, inclusiveMax === ']') : undefined,
        timezone: {
          value: timezone!,
          offset: min || max ? this.getOffset(min || max) : '',
        },
      };
    }
    if (config?.start) {
      this.start = config.start;
    }
    if (config?.end) {
      this.end = config.end;
    }
    if (config?.timezone) {
      this.timezone = config.timezone;
    }
    this.state = this.setState(this.start?.timestamp, this.end?.timestamp);
  }

  toString(): string {
    const minBound = this.start?.inclusive ? '[' : '(';
    const maxBound = this.end?.inclusive ? ']' : ')';
    const min = this.serialize(
      this.start?.date,
      this.start?.time,
      this.timezone?.offset
    );
    const max = this.serialize(
      this.end?.date,
      this.end?.time,
      this.timezone?.offset
    );
    if (!!min && !!max) {
      return `${minBound}${min},${max}${maxBound}`;
    } else if (!!min && !max) {
      return `${minBound}${min},)`;
    } else if (!min && !!max) {
      return `(,${max}${maxBound}`;
    } else {
      return '(,)';
    }
  }

  serialize(
    date: Date | undefined,
    time: string | undefined,
    offset: string = 'Z'
  ): string | undefined {
    if (!date || !time) {
      return undefined;
    }
    const day = formatNumber(date.getDate(), 'en-US', '2.');
    const month = formatNumber(date.getMonth() + 1, 'en-US', '2.');
    const year = date.getFullYear();

    return `${year}-${month}-${day}T${time}${offset}`;
  }

  parse(value: string, inclusive: boolean): ValidityDateTime {

    const timestamp = new Date(value).getTime();
    let dateTime = value.split('T');
    if (dateTime.length === 1) {
      dateTime = value.split(' ');
    }

    const dateSplit = dateTime[0].split('-');
    return {
      date: new Date(
        Number(dateSplit[0]),
        Number(dateSplit[1]) - 1,
        Number(dateSplit[2])
      ),
      time: dateTime[1].slice(0, 5),
      inclusive,
      timestamp,
      day: Number(dateSplit[2]),
      month: Number(dateSplit[1]) - 1,
      year: Number(dateSplit[0]),
    };
  }

  protected getOffset(value: string): string {

    let dateTime = value.split('T');
    if (dateTime.length === 1) {
      dateTime = value.split(' ');
    }
    const splitPlus = dateTime[1].split('+');
    if (splitPlus.length > 1) {
      return `+${splitPlus[1]}${splitPlus[1].search(/:/) < 0 ? ':00' : ''}`;
    }
    const splitMoins = dateTime[1].split('-');
    if (splitMoins.length > 1) {
      return `-${splitMoins[1]}${splitMoins[1].search(/:/) < 0 ? ':00' : ''}`;
    }
    return `+00:00}`;
  }

  private setState(
    start: number | undefined,
    end: number | undefined
  ): string {
    const now = Date.now();
    if (!start && !end) {
      return 'live';
    }
    if (!start) {
      return end && now < end ? 'live' : 'stopped';
    }
    if (!end) {
      return start && now > start ? 'live' : 'pending';
    }
    if (now > start) {
      return now < end ? 'live' : 'stopped';
    }
    return 'pending';
  }
}
